import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Snackbar,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { passportService } from '../services/passportService';
import StatusChip from '../components/common/StatusChip';
import { PassportRecord } from '../types';
import { log } from 'console';

const RegisterReport: React.FC = () => {
  const theme = useTheme();
  const { user, hasRole } = useAuth();
  const [records, setRecords] = useState<PassportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [searchParams, setSearchParams] = useState({
    passport_no: '',
    mobile_number: '',
    enrolment_no: '',
    money_receipt_no: '',
    iqama_number: '',
    employer_name: '',
    place_area: '',
    from_date: getTodayDate(),
    to_date: getTodayDate(),
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      setHasSearched(true);

      const params = {
        ...searchParams,
        page,
        limit: 10,
      };

      const response = await passportService.getRegisterReport(params as any);

      setRecords(response.results || []);
      setTotalRecords(response.count || 0);
      setTotalPages(Math.ceil((response.count || 0) / 10));
    } catch (err: any) {
      console.error('Fetch error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch records';
      setError(errorMessage);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate serial number based on current page
  const getSerialNumber = (index: number) => {
    return (page - 1) * 10 + index + 1;
  };

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const handleReset = () => {
    setSearchParams({
      passport_no: '',
      mobile_number: '',
      enrolment_no: '',
      money_receipt_no: '',
      iqama_number: '',
      employer_name: '',
      place_area: '',
      from_date: getTodayDate(),
      to_date: getTodayDate(),
    });
    setPage(1);
    setRecords([]);
    setHasSearched(false);
    setTotalRecords(0);
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    try {
      setLoading(true);
      setError(null);
      //console.log('...searchParams:', {...searchParams})
      await passportService.exportRegisterReport({ ...searchParams, format });
      //console.log('line 140:')
      setSuccessMessage(`Exported successfully!`);
    } catch (err: any) {
      // If the error is a Blob (common in file downloads), parse it safely
      if (err.data instanceof Blob && err.data.type === 'application/json') {
         const text = await err.data.text();
         const errorData = JSON.parse(text);
         setError(errorData.detail || errorData.error || 'Export failed');
      } else {
         setError(err.message || 'A network error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${parseInt(day)}-${months[parseInt(month) - 1]}-${year}`;
  };

  // Refetch when page changes
  useEffect(() => {
    if (hasSearched) {
      fetchRecords();
    }
  }, [page]);

  const canExport = hasRole('DATA_MANAGER');

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <ArticleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Register Report
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and export verified records
        </Typography>
      </Box>

      {/* Search Filters Card */}
      <Card
        sx={{
          mb: 3,
          boxShadow: theme.shadows[3],
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Search Filters
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Passport No"
                value={searchParams.passport_no}
                onChange={(e) => setSearchParams({ ...searchParams, passport_no: e.target.value })}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={searchParams.mobile_number}
                onChange={(e) => setSearchParams({ ...searchParams, mobile_number: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Enrolment No"
                value={searchParams.enrolment_no}
                onChange={(e) => setSearchParams({ ...searchParams, enrolment_no: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Money Receipt No"
                value={searchParams.money_receipt_no}
                onChange={(e) => setSearchParams({ ...searchParams, money_receipt_no: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Iqama Number"
                value={searchParams.iqama_number}
                onChange={(e) => setSearchParams({ ...searchParams, iqama_number: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Employer Name"
                value={searchParams.employer_name}
                onChange={(e) => setSearchParams({ ...searchParams, employer_name: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Place / Area"
                value={searchParams.place_area}
                onChange={(e) => setSearchParams({ ...searchParams, place_area: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              {/* Empty spacer */}
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={searchParams.from_date}
                onChange={(e) => setSearchParams({ ...searchParams, from_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={searchParams.to_date}
                onChange={(e) => setSearchParams({ ...searchParams, to_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                  fullWidth
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                  fullWidth
                >
                  Reset
                </Button>
                {canExport && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('excel')}
                      disabled={loading || !hasSearched}
                      fullWidth
                      color="success"
                    >
                      Excel
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleExport('pdf')}
                      disabled={loading || !hasSearched}
                      fullWidth
                      color="error"
                    >
                      PDF
                    </Button>
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Alert */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Records Table Card */}
      <Card
        sx={{
          boxShadow: theme.shadows[4],
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Verified Records</span>
                {hasSearched && (
                  <Chip
                    label={`${totalRecords} records`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Typography>
            {loading && <LinearProgress sx={{ flex: 1, mx: 2, maxWidth: 300 }} />}
          </Box>

          {!hasSearched ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <ArticleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Search Performed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click the "Search" button to view verified records
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 700, width: 70 }}>Sl. No.</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Passport No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Passport Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Entry Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Iqama No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Iqama Expiry</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Employer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Place/Area</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Enrolment No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Receipt No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount (JD)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {loading ? 'Loading records...' : 'No verified records found matching the search criteria'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    records.map((record, index) => (
                      <TableRow
                        key={record.passport_applicants_id}
                        hover
                        sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {getSerialNumber(index)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {record.applicants_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.applicants_type_display}
                            size="small"
                            color={record.applicants_type === 'INDIVIDUAL' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{record.passport_no}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {record.passport_type_display}
                          </Typography>
                        </TableCell>
                        <TableCell>{record.mobile_number}</TableCell>
                        <TableCell sx={{ px: 1 }}>{formatDate(record.date_of_entry)}</TableCell>
                        <TableCell>{record.iqama_number || '—'}</TableCell>
                        <TableCell sx={{ px: 1 }}>
                          {record.expiry_date_of_iqama ? formatDate(record.expiry_date_of_iqama) : '—'}
                        </TableCell>
                        <TableCell>
                          {record.applicants_type === 'INDIVIDUAL' ? 'Self' : (record.employer_name || '—')}
                        </TableCell>
                        <TableCell>{record.place_area || '—'}</TableCell>
                        <TableCell>{record.enrolment_no}</TableCell>
                        <TableCell>{record.money_receipt_no || '—'}</TableCell>
                        <TableCell>
                          {record.amount_jd ? (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              {Number(record.amount_jd).toFixed(2)}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {record.remarks || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {hasSearched && totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RegisterReport;